// PaymentForm.js
import { useState, useEffect, useMemo } from "react";
import { PMT, COUNTRY_CODES } from "../classes/Enum";
import { Elements } from "@stripe/react-stripe-js";
import { CURRENCY_CODE } from "../config";
import StripePaymentComponent from "./StripePayment";
import { useCart } from "./Context";
import { useAuth } from "./AuthProvider";
import ShippingOptions from "./cart/ShippingOptions";
import { applyCoupon } from "../api/coupon";
import PaymentMethodComponent from "./PaymentMethod";
import { getDisplayPrice } from "../helper/products";
import { CartItem } from "../classes/Item";

const PaymentForm = ({
  grandTotal = 0,
  stripePromise,
  checkoutMeta,
  onCheckoutMetaChange,
  cartState,
  setCartState,
  paymentMethod,
  setPaymentMethod,
  setError,
  setLoading,
  customerInfo,
  setCustomerInfo,
  setCart,
}) => {
  
  const { cart } = useCart();
  const { user, address } = useAuth();

  const [copyBillingFromShipping, setCopyBillingFromShipping] = useState(true);
  const [isShippingCollapsed, setIsShippingCollapsed] = useState(false);
  const [isBillingCollapsed, setIsBillingCollapsed] = useState(true);

  const [couponError, setCouponError] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Prefill from global user/address ONCE when they appear/refresh
  useEffect(() => {
    const next = { ...customerInfo };

    if (user) {
      next.tiktokName = user.tiktokName ?? user.tiktok_username ?? "";
      next.firstName = user.firstName ?? user.first_name ?? "";
      next.lastName = user.lastName ?? user.last_name ?? "";
      next.email = user.email ?? "";
    }

    if (address) {
      next.shippingAddress = address.address ?? "";
      next.shippingSuburb = address.suburb ?? "";
      next.shippingPostcode = address.postcode ?? "";
      next.shippingState = address.state ?? "";
      next.shippingCountry = address.country ?? "";
      next.billingAddress = address.address ?? "";
      next.billingSuburb = address.suburb ?? "";
      next.billingPostcode = address.postcode ?? "";
      next.billingState = address.state ?? "";
      next.billingCountry = address.country ?? "";
    }

    setCustomerInfo(next);
  }, [user, address]);

  // Keep billing synced if toggle is on
  useEffect(() => {
    if (!copyBillingFromShipping) return;
    setCustomerInfo({
      billingAddress: customerInfo.shippingAddress,
      billingSuburb: customerInfo.shippingSuburb,
      billingPostcode: customerInfo.shippingPostcode,
      billingState: customerInfo.shippingState,
      billingCountry: customerInfo.shippingCountry,
    });
  }, [
    copyBillingFromShipping,
    customerInfo.shippingAddress,
    customerInfo.shippingSuburb,
    customerInfo.shippingPostcode,
    customerInfo.shippingState,
    customerInfo.shippingCountry,
  ]);

  const isShippingFilled =
    customerInfo.firstName &&
    customerInfo.lastName &&
    customerInfo.shippingAddress &&
    customerInfo.shippingSuburb &&
    customerInfo.shippingPostcode &&
    customerInfo.shippingState &&
    customerInfo.email &&
    customerInfo.shippingCountry;

  const isBillingFilled =
    customerInfo.billingAddress &&
    customerInfo.billingSuburb &&
    customerInfo.billingPostcode &&
    customerInfo.billingState &&
    customerInfo.billingCountry;

  useEffect(() => {
    onCheckoutMetaChange({ readyToCheckout: (!!isShippingFilled && !!isBillingFilled) });
  }, [isShippingFilled, isBillingFilled]);

  // Stripe Elements expects amount in the smallest currency unit (cents).
  // grandTotal is already cents (items + shipping - flat discount).
  const options = useMemo(
    () => ({
      mode: "payment",
      currency: CURRENCY_CODE,
      amount: Math.max(1, Math.round(Number(grandTotal) || 0)),
      appearance: {
        theme: "night",
        variables: {
          colorPrimary: "#007BFF",
          fontSizeBase: "16px",
          borderRadius: "8px",
        },
      },
      layout: { type: "accordion", radios: true, spacedAccordionItems: true },
    }),
    [grandTotal]
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCustomerInfo({[name]: value });
  };

const handleApplyCoupon = async () => {
  const code = (couponInput || checkoutMeta.couponCode || "").trim().toUpperCase();
  setCouponError(null);

  if (!code) return;

  setIsProcessing(true);
  try {
    const result = await applyCoupon(code, cart);

    if (!result.success) {
      setCouponError(result.message || "Invalid or expired coupon.");
      onCheckoutMetaChange({
        couponCode: "",
        discountAmount: 0,
        flatDiscount: 0,
      });
      return;
    }

    setCart(result.cart);
    setCouponInput(result.couponCode || code);
    onCheckoutMetaChange({
      couponCode: result.couponCode || code,
      discountAmount: result.discountAmount || 0,
      flatDiscount: result.flatDiscount || 0,
    });
  } catch (err) {
    console.error(err);
    setCouponError("Invalid or expired coupon.");
    onCheckoutMetaChange({
      couponCode: "",
      discountAmount: 0,
      flatDiscount: 0,
    });
  } finally {
    setIsProcessing(false);
  }
};


  const clearCoupon = () => {
    setCouponInput("");
    setCouponError(null);
    setCart(
      cart.map(
        (item) =>
          new CartItem(
            item.productId,
            item.condition,
            item.variant,
            item.count,
            null,
            null,
            item.type
          )
      )
    );
    onCheckoutMetaChange({
      couponCode: "",
      discountAmount: 0,
      flatDiscount: 0,
    });
  };

  return (
    <div id="checkout-section" className="hide-scrollbar">
      <div className="checkout-section">
          <h2>
            1. Customer
            {isShippingFilled && isBillingFilled ? (
              <span style={{ color: "limegreen", marginLeft: 10 }}>✔</span>
            ) : (
              <span style={{ color: "red", marginLeft: 10 }}>✘</span>
            )}
          </h2>
          <button
            type="button"
            className="cart-button minor tertiary-active"
            onClick={() => setIsShippingCollapsed((v) => !v)}
            style={{ marginLeft: 10 }}
          >
            {isShippingCollapsed ? "Change" : "Collapse"}
          </button>

          {!isShippingCollapsed && (
            <form>
              <div className="row">
                <input
                  type="text"
                  name="tiktokName"
                  value={customerInfo.tiktokName}
                  onChange={handleInputChange}
                  placeholder="TikTok Name"
                />
              </div>

              <div className="row">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={customerInfo.firstName}
                  onChange={handleInputChange}
                  required
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={customerInfo.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="row">
                <input
                  type="text"
                  name="email"
                  placeholder="Email"
                  value={customerInfo.email}
                  onChange={handleInputChange} 
                  required
                />
              </div>

              <div className="row">
                <input
                  type="text"
                  name="shippingAddress"
                  placeholder="Address"
                  value={customerInfo.shippingAddress}
                  onChange={handleInputChange} 
                  required
                />
                <input
                  type="text"
                  name="shippingSuburb"
                  placeholder="Suburb"
                  value={customerInfo.shippingSuburb}
                  onChange={handleInputChange} 
                  required
                />
              </div>

              <div className="row">
                <input
                  type="text"
                  name="shippingPostcode"
                  placeholder="Postcode"
                  value={customerInfo.shippingPostcode}
                  onChange={handleInputChange}                  
                required
                />
                <input
                  type="text"
                  name="shippingState"
                  placeholder="State"
                  value={customerInfo.shippingState}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="row">
                <select
                  name="shippingCountry"
                  value={customerInfo.shippingCountry}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Country</option>
                  {COUNTRY_CODES.map(({ code, name }) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </form>
          )}

          {/* {!isShippingCollapsed && (
            <form>
              <div className="slider-container">
                <div className={`option ${copyBillingFromShipping ? "active" : ""}`}>
                  Copy Shipping to Billing?
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={copyBillingFromShipping}
                    onChange={() => setCopyBillingFromShipping((v) => !v)}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="row">
                <input
                  type="text"
                  name='billingAddress'
                  placeholder="Billing Address"
                  value={customerInfo.billingAddress}
                  onChange={handleInputChange}
                  required
                  disabled={copyBillingFromShipping}
                />
                <input
                  type="text"
                  name='billingSuburb'
                  placeholder="Billing City"
                  value={customerInfo.billingSuburb}
                  onChange={handleInputChange}
                  required
                  disabled={copyBillingFromShipping}
                />
              </div>

              <div className="row">
                <input
                  type="text"
                  name='billingPostcode'
                  placeholder="Billing Postcode"
                  value={customerInfo.billingPostcode}
                  onChange={handleInputChange}
                  required
                  disabled={copyBillingFromShipping}
                />
                <input
                  type="text"
                  placeholder="Billing State"
                  name='billingState'
                  value={customerInfo.billingState}
                  onChange={handleInputChange}
                  required
                  disabled={copyBillingFromShipping}
                />
              </div>

              <div className="row">
                <select
                  name='billingCountry'
                  value={customerInfo.billingCountry}
                  onChange={handleInputChange}
                  required
                  disabled={copyBillingFromShipping}
                >
                  <option value="">Select Country</option>
                  {COUNTRY_CODES.map(({ code, name }) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </form>
          )} */}
      </div>
      <ShippingOptions value={checkoutMeta.shippingCost} onSelect={onCheckoutMetaChange} country={customerInfo.shippingCountry} />
      <div className="checkout-section">
          <h2>3. Coupon</h2>

          <div className="row">
            <input
              type="text"
              name="coupon"
              placeholder="Enter coupon code"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              autoComplete="off"
            />
            <button
              type="button"
              className=""
              onClick={() => handleApplyCoupon()}
              disabled={isProcessing}
              style={{ marginLeft: 10 }}
            >
              <h3>Apply</h3>
            </button>

            {checkoutMeta.couponCode && (
              <button
                type="button"
                className="remove-coupon-button"
                onClick={clearCoupon}
                disabled={isProcessing}
                style={{ marginLeft: 10 }}
              >
                Clear
              </button>
            )}
          </div>

          {checkoutMeta.couponCode && (
            <div style={{ marginTop: 8, color: "limegreen" }}>
              Applied: <strong>{checkoutMeta.couponCode}</strong>
              {checkoutMeta.discountAmount > 0 && (
                <> (−${getDisplayPrice(checkoutMeta.discountAmount)})</>
              )}
            </div>
          )}
          {couponError && <div className="error">{couponError}</div>}
        </div>
      {grandTotal > 0 && (
        <div className="checkout-section">
          <PaymentMethodComponent setPaymentMethod={setPaymentMethod} shippingOption={checkoutMeta.shippingCost} />
        </div>
      )}

      {grandTotal <= 0 && checkoutMeta.shippingCost !== null && (
        <div className="checkout-section">
          <h3>No payment required — your coupon covers this order.</h3>
          <h3>Press the button below to complete checkout.</h3>
        </div>
      )}

      {grandTotal > 0 && checkoutMeta.shippingCost !== null && paymentMethod && (
      <div className="checkout-section">

        <Elements stripe={stripePromise} options={options}>
          {paymentMethod === PMT.CARD && (
            <StripePaymentComponent
              user={user}
              setIsProcessing={setIsProcessing}
              setError={setError}
              grandTotal={grandTotal}
              cart={cart}
              customerInfo={customerInfo}
              shippingPaid={checkoutMeta.shippingCost}
              couponCode={checkoutMeta.couponCode}
              cartState={cartState}
              setCartState={setCartState}
              onCheckoutMetaChange={onCheckoutMetaChange}
              setLoading={setLoading}
            />
          )}

          {paymentMethod === PMT.PAYID && (
            <div className="checkout-section">
              <h3>Generate your order to receive PayID payment instructions.</h3>
              <h3>Your items will be reserved while you complete payment.</h3>
              <div className="padding-bottom"/>
            </div>
          )}
        </Elements>
      </div>
      )}
  </div>
  );
};

export default PaymentForm;
